// Schema-Probe. Welche Teile des Preismodells kennt das Backend dieser Stage?
// Payment.plan und die neuen PRODUCTS (CUSTOM_REPORTS, LANGUAGE_FR,
// LANGUAGE_NL) kommen erst mit dem Schema-Deploy. Fragt eine Query ein Feld
// ab, das es nicht gibt, lehnt AppSync die ganze Query ab. Deshalb entscheidet
// diese Probe, ob die Selection Sets mit plan laufen dürfen.
//
// Zuerst per Introspection. Ist die auf AppSync gesperrt, prüfen zwei kleine
// Queries das Feld und die Enum-Werte direkt. Beide lesen nichts (ID und
// Partner gibt es nicht) und scheitern nur an der Validierung, wenn Feld oder
// Wert fehlen.
//
// Eindeutig ist nur ein Erfolg oder ein Validierungsfehler. Netz- und
// HTTP-Fehler sagen nichts über das Schema, dann wirft die Probe einen
// SchemaProbeError. useSchemaCapabilities wiederholt sie und zeigt danach
// „Prüfung fehlgeschlagen", statt still „Feld fehlt" anzunehmen.
import { API, graphqlOperation } from 'aws-amplify'

import { LEGACY_SCHEMA_PRODUCTS, NEW_PRODUCTS } from '../constants/products'

// Stand, solange die Probe läuft oder gescheitert ist. Ohne Ergebnis laufen
// nur die Selection Sets ohne plan.
export const SAFE_CAPABILITIES = Object.freeze({
  paymentPlan: false,
  products: null,
  source: 'failed'
})

export class SchemaProbeError extends Error {
  constructor(message, cause) {
    super(message)
    this.name = 'SchemaProbeError'
    this.cause = cause
  }
}

export const PROBE_RESULTS = {
  PRESENT: 'present',
  MISSING: 'missing',
  UNKNOWN: 'unknown'
}

export const schemaCapabilitiesQuery = /* GraphQL */ `
  query SchemaCapabilities {
    payment: __type(name: "Payment") {
      fields {
        name
      }
    }
    products: __type(name: "PRODUCTS") {
      enumValues {
        name
      }
    }
  }
`

const PROBE_ID = '__schema_probe__'

export const paymentPlanProbeQuery = /* GraphQL */ `
  query PaymentPlanProbe {
    getPayment(id: "${PROBE_ID}") {
      id
      plan
    }
  }
`

// Enum-Literale werden vor dem Resolver validiert. Fehlt ein Wert, scheitert
// die Query, sonst liefert der leere Partner keine Zeilen. Kennt das Schema
// den Filter in dieser Form nicht, scheitert sie ebenfalls und products
// bleibt unbekannt (sicher).
export const newProductsProbeQuery = /* GraphQL */ `
  query NewProductsProbe {
    ${NEW_PRODUCTS.map(
      (product) => `${product.toLowerCase()}: paymentByPartnerIdByCreatedAt(
      partner_id: "${PROBE_ID}"
      filter: { products: { contains: ${product} } }
      limit: 1
    ) {
      items {
        id
      }
    }`
    ).join('\n    ')}
  }
`

const defaultGraphql = (queryString) =>
  API.graphql(graphqlOperation(queryString))

const namesOf = (list) =>
  Array.isArray(list)
    ? list
        .map((entry) => entry?.name)
        .filter((name) => typeof name === 'string')
    : null

/**
 * Ergebnis der Introspection in die Form der Probe. Ohne Typ Payment oder
 * PRODUCTS null, dann gilt die Introspection als nicht verwertbar.
 */
export const parseIntrospection = (data) => {
  const fields = namesOf(data?.payment?.fields)
  const products = namesOf(data?.products?.enumValues)
  if (!fields || !products) return null

  return {
    paymentPlan: fields.includes('plan'),
    products: [...products].sort(),
    source: 'introspection'
  }
}

const VALIDATION_PATTERN =
  /validation error|FieldUndefined|WrongType|UnknownType|UnknownArgument/i

const isValidationEntry = (entry) =>
  entry?.errorType === 'ValidationError' ||
  VALIDATION_PATTERN.test(String(entry?.message || ''))

// Amplify verpackt Netz- und HTTP-Fehler als GraphQLError mit originalError.
// Liefert AppSync die Validierung mit HTTP 400, steht sie dort im Body.
const errorEntriesOf = (error) => {
  const entries = Array.isArray(error?.errors) ? error.errors : []
  const nested = entries.flatMap((entry) => {
    const body = entry?.originalError?.response?.data
    return Array.isArray(body?.errors) ? body.errors : []
  })
  return { entries, nested }
}

/**
 * Was ein Fehler über das Schema sagt.
 * - missing, AppSync hat das Dokument nicht validiert (Feld oder Wert fehlt)
 * - present, ein Resolver ist gelaufen und hat einen Fehler gemeldet. Das
 *   Dokument war also gültig.
 * - unknown, Netz, HTTP oder Unbekanntes. Dazu sagt der Fehler nichts.
 */
export const classifyProbeError = (error) => {
  const { entries, nested } = errorEntriesOf(error)
  if ([...entries, ...nested].some(isValidationEntry)) {
    return PROBE_RESULTS.MISSING
  }
  const resolverErrors =
    entries.length > 0 &&
    entries.every(
      (entry) =>
        typeof entry?.errorType === 'string' &&
        entry.errorType !== '' &&
        !entry.originalError
    )
  return resolverErrors ? PROBE_RESULTS.PRESENT : PROBE_RESULTS.UNKNOWN
}

const probe = async (graphql, queryString) => {
  try {
    await graphql(queryString)
    return PROBE_RESULTS.PRESENT
  } catch (error) {
    return classifyProbeError(error)
  }
}

/**
 * @param {{ graphql?: (query: string) => Promise<{ data: object }> }} [deps]
 *   graphql nur für Tests
 * @returns {Promise<{ paymentPlan: boolean, products: string[]|null,
 *   source: 'introspection'|'probe' }>}
 *   products null heißt, die neuen Werte fehlen. Aufrufer nehmen dann nur die
 *   Werte, die jede Stage schon kannte (availableProductsFor in
 *   src/helper/pricing.js).
 * @throws {SchemaProbeError} wenn weder Introspection noch Probe eine
 *   eindeutige Antwort geben, etwa bei Netzfehlern
 */
export async function fetchSchemaCapabilities({
  graphql = defaultGraphql
} = {}) {
  try {
    const { data } = await graphql(schemaCapabilitiesQuery)
    const parsed = parseIntrospection(data)
    if (parsed) return parsed
  } catch (_) {
    // Introspection gesperrt oder Netzfehler, weiter mit der direkten Probe
  }

  const [paymentPlan, newProducts] = await Promise.all([
    probe(graphql, paymentPlanProbeQuery),
    probe(graphql, newProductsProbeQuery)
  ])

  if (
    paymentPlan === PROBE_RESULTS.UNKNOWN ||
    newProducts === PROBE_RESULTS.UNKNOWN
  ) {
    throw new SchemaProbeError(
      'Schema-Prüfung fehlgeschlagen, AppSync hat nicht eindeutig geantwortet'
    )
  }

  return {
    paymentPlan: paymentPlan === PROBE_RESULTS.PRESENT,
    products:
      newProducts === PROBE_RESULTS.PRESENT
        ? [...LEGACY_SCHEMA_PRODUCTS, ...NEW_PRODUCTS].sort()
        : null,
    source: 'probe'
  }
}
